// src/components/FolderNameModal.tsx
import React, { useEffect, useState } from "react";

type Props = {
  open: boolean;
  mode?: "create" | "rename";
  title?: string;
  initialValue?: string;
  confirmText?: string;
  onClose: () => void;
  // parent may return anything (BackendFolder, void, Promise<...>, etc.)
  onConfirm: (value: string) => Promise<unknown> | unknown;
};

export default function FolderNameModal({
  open,
  mode,
  title,
  initialValue = "",
  confirmText,
  onClose,
  onConfirm,
}: Props) {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  // lock scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const defaultConfirm = mode === "create" ? "Create" : mode === "rename" ? "Rename" : "Save";
  const confirmLabel = confirmText ?? defaultConfirm;
  const headerTitle = title ?? (mode === "create" ? "Create folder" : mode === "rename" ? "Rename folder" : "Folder");

  const handleConfirm = async () => {
    const v = value.trim();
    if (!v) return;
    setError(null);
    try {
      setIsSaving(true);
      await Promise.resolve(onConfirm(v));
      setIsSaving(false);
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      setError(err?.message ?? "Operation failed");
    }
  };

return (
    <>
      <div
        className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm"
        onClick={() => {
          if (!isSaving) onClose();
        }}
      />

      <div
        className="
        fixed
        left-1/2 top-1/2
        z-[10000]
        -translate-x-1/2 -translate-y-1/2
        w-[400px] max-w-[92%]
        rounded-2xl
        bg-white dark:bg-[#1a1a1a]
        border border-gray-200 dark:border-[#333]
        shadow-2xl
        text-black dark:text-white
        font-['Product_Sans']
      "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h3 className="text-[22px] font-medium tracking-wide">{headerTitle}</h3>
          <button
            onClick={() => {
              if (!isSaving) onClose();
            }}
            className="text-gray-500 hover:text-black dark:hover:text-white transition shrink-0"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          {mode === "create" && (
            <label className="block text-[11px] font-bold uppercase tracking-widest text-black dark:text-white mb-2">
              Folder Name
            </label>
          )}
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="
              w-full rounded-lg bg-transparent px-4 py-3.5 text-sm text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500
              border border-black dark:border-white focus:outline-none transition
            "
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!isSaving) handleConfirm();
              }
            }}
            disabled={isSaving}
          />
          {error && <div className="mt-2 text-sm text-red-500">{error}</div>}
        </div>

        {/* Footer */}
        <div className="flex justify-center sm:justify-end gap-3 px-6 pb-6">
          <button
            onClick={() => {
              if (!isSaving) onClose();
            }}
            className="px-6 py-2.5 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition text-[13px] font-bold"
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition border ${
              isSaving
                ? "bg-gray-400 border-gray-400 text-white cursor-wait dark:bg-gray-600 dark:border-gray-600"
                : "bg-black border-black text-white dark:bg-white dark:border-white dark:text-black hover:opacity-90"
            }`}
            disabled={isSaving}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.2" />
                  <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
                Saving...
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </>
  );
}
